import { ColDef, IGetRowsParams } from 'ag-grid-community';
import { tableApi } from '../../../../apis/table/table-api';
import { TableProvider } from '../wtt-table';
import { translate } from '../../../../apis/localization/localization-service';
import { PresenterField, PresenterTable } from '../../../../apis/table/presenter-schema-definition';
import { WttDropDownCellEditor } from '../cell-editors/wtt-drop-down-cell-editor';
import { LOADING_CELL_RENDERER_SELECTOR } from '../cell-renderer/cell-renderer-selectors';
import {
  DATE_VALUE_FORMATTER,
  ENUM_VALUE_FORMATTER,
  FORMATTED_VALUE_TOOLTIP_GETTER,
  STRING_TOOLTIP_VALUE_GETTER,
} from '../value-formatters';
import { PartModelRowStateRenderer } from '../cell-renderer/part-model-row-state-renderer';
import { handleDefaultApiErrors } from '../../../../apis/api';
import _ from 'lodash';

export type ClassColDefOverrides = DeepReadonly<Record<string, (field: PresenterField) => ColDef>>

export const CLASS_COL_DEF_OVERRIDES: ClassColDefOverrides = {
  'de.lexcom.wtt.backend.valuedriver.categories.model.SubHpgModel': () => ({
    valueFormatter: (params) => params.value?.subHpg,
  }),
  'de.lexcom.wtt.backend.masterdata.model.PartModel$RowState': () => ({
    cellRenderer: PartModelRowStateRenderer,
    cellClass: 'icon-cell',
    tooltipValueGetter: (params) => params.value
      ? translate('PartsData.ROWSTATE.' + params.value)
      : undefined,
  }),
};

/**
 * Column definitions with option to override the column index.
 */
export type ColDefWithIndex = ColDef & {
  /**
   * Column index override. If set, this column is positioned before any other columns **without** overridden index and in the order relative
   * to other columns **with** an overwritten index.
   */
  colIndexOverride?: number
}

/**
 * {@link TableProvider} implementation that uses the backend's UniversalController infrastructure.
 * Generates column definitions based on the model defined in the backend.
 */
export class UniversalControllerTableProvider implements TableProvider {

  /**
   *
   * @param className Class name of the resource to display
   * @param colDefOverrides Overrides for columns (e.g. modify sortability or how data is displayed).
   * Expects a map with the properties name ({@link PresenterField#name}) as key and a partial {@link ColDef} as value.
   * '*' can be used as key to override a value for all columns.
   * @param _tableApi Overridable {@link TableApi} for testing purposes.
   */
  constructor(
    private className: string,
    private colDefOverrides: Record<string, Partial<ColDefWithIndex>> = {},
    private _tableApi = tableApi,
  ) {
  }

  public readonly columnTypes = {
    'ALPHA': {},
    'NUMERIC': {},
    'BOOL': {},
    'CLASS': {},
    'ENUM': {},
    'DATE': {},
  };

  async getColumnDefinitions(): Promise<ColDef[]> {

    const schemaDefinitionResponse = await handleDefaultApiErrors(
      this._tableApi.getSchemaDefinition(this.className),
    );

    if (schemaDefinitionResponse.status === 'RESOLVED_ERROR') {
      return [];
    }

    return _.chain(schemaDefinitionResponse.schemaDefinition.fields)
      .filter(field => field.visibility === 'VISIBLE')
      .map((field) => this.createColDefFromPresenterField(
        field, schemaDefinitionResponse.schemaDefinition.table,
      ))
      .orderBy(field => field.colIndexOverride ?? Infinity)
      .value();
  }

  /**
   * Builds a column definition based on a {@link PresenterField} with all overrides already being applied.
   *
   * Overrides are applied in the following order (highest beats lowest):
   * 1. Settings derived from {@link PresenterField}
   * 2. Overrides defined for the type {@link getTypeSpecificColDefOverrides}
   * 3. Class specific overrides {@link CLASS_COL_DEF_OVERRIDES} (applies to enums and objects)
   * 4. Overrides for all columns ('*' as key)
   * 5. Overrides specific to that column (field name as key)
   *
   * @param field presenter field definition
   * @param table presenter table definition
   * @private
   */
  private createColDefFromPresenterField(field: PresenterField, table: PresenterTable): ColDefWithIndex {
    return {
      colId: field.field,
      field: field.field,
      headerName: this.getHeaderName(field, table),
      editable: field.editable,
      sortable: true,
      type: field.type,
      cellRendererSelector: LOADING_CELL_RENDERER_SELECTOR,
      tooltipValueGetter: STRING_TOOLTIP_VALUE_GETTER,
      ...this.getTypeSpecificColDefOverrides(field),
      ...this.colDefOverrides['*'],
      ...this.colDefOverrides[field.name],
    };
  }

  /**
   * Returns the already translated header name for a column. Returns <code>&lt;TABLE_NAME&gt;.&lt;FIELD_NAME&gt;</code>.
   * @param field presenter field definition
   * @param table presenter table definition
   * @protected
   */
  protected getHeaderName(field: PresenterField, table: PresenterTable): string {
    return translate(`${ table.name }.${ field.name }`);
  }

  private getTypeSpecificColDefOverrides(field: PresenterField): ColDef {
    switch (field.type) {
      case 'ALPHA':
        return {
          cellDataType: 'text',
        };
      case 'NUMERIC':
        return {
          cellDataType: 'number',
        };
      case 'BOOL':
        return {
          cellDataType: 'boolean',
        };
      case 'CLASS':
        return {
          ...this.getClassSpecificColDefOverrides(field),
        };
      case 'ENUM':
        return {
          cellDataType: 'text',
          valueFormatter: ENUM_VALUE_FORMATTER,
          cellEditor: WttDropDownCellEditor,
          cellEditorParams: {
            values: field.values || [],
          },
          ...this.getClassSpecificColDefOverrides(field),
        };
      case 'DATE':
        return {
          cellDataType: 'date',
          valueFormatter: DATE_VALUE_FORMATTER,
          tooltipValueGetter: FORMATTED_VALUE_TOOLTIP_GETTER,
          cellEditor: 'agDateStringCellEditor',
        };
      default:
        return {};
    }
  }

  private getClassSpecificColDefOverrides(field: PresenterField): ColDef | undefined {
    if (field.class && CLASS_COL_DEF_OVERRIDES[field.class]) {
      return CLASS_COL_DEF_OVERRIDES[field.class](field);
    } else {
      return undefined;
    }
  }

  getRows(params: IGetRowsParams): void {
    const pageSize = params.endRow - params.startRow;
    const pageNo = Math.floor(params.startRow / pageSize);

    this._tableApi
      .getPaginatedData(
        this.className,
        pageNo,
        pageSize,
        params.sortModel[0]?.colId,
        params.sortModel[0]?.sort,
      )
      .then(handleDefaultApiErrors)
      .then(response => {
        if (response.status === 'OK') {
          params.successCallback(response.data, response.total);
        } else {
          params.failCallback();
        }
      });
  }

}
